import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { XMarkIcon, PlayIcon, BoltIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { buildGamingDataUrl } from './alertContextUtils'

function defaultTradingDay() {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

function fmtMoney(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—'
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

function fmtNum(n, digits = 2) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—'
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: digits })
}

const COVERAGE_STYLES = {
  hit: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  miss: 'border-gray-200 bg-white text-gray-700',
  error: 'border-red-300 bg-red-50 text-red-900',
  skipped: 'border-amber-200 bg-amber-50 text-amber-900',
  missing: 'border-red-200 bg-red-50 text-red-800',
}

function coverageLabel(row) {
  if (row.status === 'hit') return `${row.hits} hit${row.hits === 1 ? '' : 's'}`
  if (row.status === 'miss') return '0 hits'
  if (row.status === 'error') return 'error'
  if (row.status === 'skipped') return 'skipped'
  if (row.status === 'missing') return 'not found'
  return row.status
}

/** Match house / bare floor / machine_pk on dry-run result rows. */
function matchesHouseFilter(row, filterRaw) {
  const filter = String(filterRaw || '').trim().toLowerCase()
  if (!filter) return true
  const bare = filter.replace(/^0+/, '') || '0'
  const candidates = [row.egm_pk, row.house_number, row.vendor_machine_pk, row.session_pk]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())
  return candidates.some((v) => {
    const vBare = v.replace(/^0+/, '') || '0'
    return v === filter || vBare === bare || v.includes(filter) || v.endsWith(filter.padStart(Math.min(6, filter.length + 2), '0'))
  })
}

function RuleCoveragePanel({ coverage }) {
  if (!coverage?.length) {
    return (
      <div className="h-full flex flex-col border-l border-gray-200 bg-gray-50 p-3">
        <div className="text-xs font-medium text-gray-700 mb-1">Per-rule coverage</div>
        <p className="text-[11px] text-gray-500">Run dry-run to see which rules hit.</p>
      </div>
    )
  }
  const hitN = coverage.filter((r) => r.status === 'hit').length
  const missN = coverage.filter((r) => r.status === 'miss').length
  const errN = coverage.filter((r) => r.status === 'error' || r.status === 'missing').length
  const skipN = coverage.filter((r) => r.status === 'skipped').length
  return (
    <div className="h-full min-h-0 flex flex-col border-l border-gray-200 bg-gray-50">
      <div className="shrink-0 p-3 border-b border-gray-200">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          Per-rule coverage
        </div>
        <div className="text-[11px] text-gray-500 mt-1">
          {coverage.length} selected
          {' · '}
          <span className="text-emerald-700">{hitN} hit</span>
          {' · '}
          <span>{missN} no hit</span>
          {errN > 0 && <>{' · '}<span className="text-red-700">{errN} error</span></>}
          {skipN > 0 && <>{' · '}<span className="text-amber-700">{skipN} skipped</span></>}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1.5">
        {coverage.map((row) => (
          <div
            key={row.id}
            title={[row.name, row.kind, coverageLabel(row), row.error || ''].filter(Boolean).join(' — ')}
            className={`rounded border px-2.5 py-2 text-[11px] ${COVERAGE_STYLES[row.status] || COVERAGE_STYLES.miss}`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono font-semibold break-all">{row.id}</span>
              <span className="font-semibold tabular-nums shrink-0">{coverageLabel(row)}</span>
            </div>
            <div className="opacity-80 mt-0.5 leading-snug">{row.name}</div>
            {row.kind && row.kind !== 'unknown' && (
              <div className="opacity-60 mt-0.5 text-[10px] uppercase tracking-wide">{row.kind}</div>
            )}
            {row.error && (
              <div className="mt-1 text-red-800/90 text-[10px] leading-snug">{row.error}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/** Widen interval window slightly for Gaming Data filters. */
function intervalWindowIso(start, end) {
  const s = start ? new Date(start) : null
  const e = end ? new Date(end) : null
  if (!s || Number.isNaN(s.getTime())) return { from: '', to: '' }
  const from = new Date(s.getTime() - 5 * 60 * 1000).toISOString()
  const toBase = e && !Number.isNaN(e.getTime()) ? e : new Date(s.getTime() + 15 * 60 * 1000)
  const to = new Date(toBase.getTime() + 5 * 60 * 1000).toISOString()
  return { from, to }
}

function intervalRowKey(r) {
  return `${r.session_pk || ''}::${r.interval_start || ''}`
}

/** Widen session window slightly for Gaming Data filters. */
function sessionWindowIso(start, end) {
  const s = start ? new Date(start) : null
  const e = end ? new Date(end) : null
  if (!s || Number.isNaN(s.getTime())) return { from: '', to: '' }
  const from = new Date(s.getTime() - 15 * 60 * 1000).toISOString()
  const toBase = e && !Number.isNaN(e.getTime()) ? e : s
  const to = new Date(toBase.getTime() + 15 * 60 * 1000).toISOString()
  return { from, to }
}

export default function RuleDryRunModal({
  open,
  ruleIds = [],
  ruleLabel = '',
  draftOnly = false,
  onClose,
}) {
  const [tradingDay, setTradingDay] = useState(defaultTradingDay)
  const [preparePipeline, setPreparePipeline] = useState(true)
  const [prepareRollup, setPrepareRollup] = useState(true)
  const [preflight, setPreflight] = useState(null)
  const [result, setResult] = useState(null)
  const [loadingPreflight, setLoadingPreflight] = useState(false)
  const [running, setRunning] = useState(false)
  const [runMode, setRunMode] = useState(null) // 'dry' | 'live'
  const [expandedSession, setExpandedSession] = useState(null)
  const [expandedInterval, setExpandedInterval] = useState(null)
  const [houseFilter, setHouseFilter] = useState('')

  const filteredResults = useMemo(() => {
    const rows = Array.isArray(result?.results) ? result.results : []
    if (!houseFilter.trim()) return rows
    return rows.filter((r) => matchesHouseFilter(r, houseFilter))
  }, [result, houseFilter])

  const filteredIntervalResults = useMemo(() => {
    const rows = Array.isArray(result?.interval_results) ? result.interval_results : []
    if (!houseFilter.trim()) return rows
    return rows.filter((r) => matchesHouseFilter(r, houseFilter))
  }, [result, houseFilter])

  const loadPreflight = useCallback(async (day) => {
    setLoadingPreflight(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/rules/dry-run/preflight?trading_day=${encodeURIComponent(day)}`)
      )
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      setPreflight(json.data || json)
    } catch (err) {
      setPreflight(null)
      toast.error(err.message || 'Preflight failed')
    } finally {
      setLoadingPreflight(false)
    }
  }, [])

  useEffect(() => {
    if (!open) {
      setPreflight(null)
      setResult(null)
      setRunMode(null)
      setExpandedSession(null)
      setExpandedInterval(null)
      setHouseFilter('')
      return
    }
    setTradingDay(defaultTradingDay())
    setResult(null)
    setRunMode(null)
    setExpandedSession(null)
    setExpandedInterval(null)
    setHouseFilter('')
  }, [open])

  useEffect(() => {
    if (!open || !tradingDay) return
    const t = setTimeout(() => loadPreflight(tradingDay), 300)
    return () => clearTimeout(t)
  }, [open, tradingDay, loadPreflight])

  const handleRun = async (persist) => {
    if (draftOnly && persist) return
    if (persist) {
      const n = ruleIds?.length || 0
      const ok = window.confirm(
        `Run LIVE for ${n || 'selected'} rule(s) on ${tradingDay}?\n\n` +
          'This creates Monitor Safe alerts (staging → alerts). ' +
          'Sessions that already have an open alert for the same rule are skipped.\n\n' +
          'Dry-run first if you have not reviewed hits yet.'
      )
      if (!ok) return
    }
    setRunning(true)
    setRunMode(persist ? 'live' : 'dry')
    setResult(null)
    try {
      const body = {
        trading_day: tradingDay,
        rule_ids: ruleIds?.length ? ruleIds : ['all'],
        prepare_pipeline: preparePipeline,
        prepare_rollup: prepareRollup,
        persist: draftOnly ? false : !!persist,
        draft_mode: draftOnly,
      }
      const res = await authFetch(getLocalApiUrl('/api/rules/dry-run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!res.ok && json.success !== true) {
        setResult({ ok: false, error: json.error || `HTTP ${res.status}`, preflight: json.preflight })
        if (json.preflight) setPreflight(json.preflight)
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      setResult(json)
      setPreflight(json.preflight || preflight)
      if (json.ok) {
        if (persist) {
          const staged = json.summary?.alerts_staged ?? 0
          const skipped = json.summary?.alerts_skipped_existing ?? 0
          toast.success(
            `Live run complete — ${staged} alert(s) staged` +
              (skipped ? `, ${skipped} skipped (already open)` : '')
          )
        } else {
          toast.success('Dry-run complete')
        }
      } else {
        toast.error(json.error || 'Run could not complete')
      }
    } catch (err) {
      toast.error(err.message || 'Run failed')
    } finally {
      setRunning(false)
    }
  }

  if (!open) return null

  const blocking = preflight?.blocking || []
  const warnings = preflight?.warnings || []
  const ruleCount = ruleIds?.length || 0
  const coverage = result
    ? (result.rule_coverage || result.summary?.rule_coverage || [])
    : []
  const showCoveragePanel = !!result

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex p-2 sm:p-4">
      <div className="m-auto w-full max-w-6xl bg-white rounded-lg shadow-lg max-h-[92vh] h-[92vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between shrink-0">
          <div>
            <div className="text-base font-medium">{draftOnly ? 'Draft rule dry-run' : 'Rules run'}</div>
            {ruleLabel && <div className="text-sm text-gray-500 mt-0.5">{ruleLabel}</div>}
            {draftOnly && (
              <p className="text-xs text-amber-700 mt-1">Draft only — no alerts are created (dry-run).</p>
            )}
          </div>
          <button type="button" className="text-gray-600" onClick={onClose} aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className={`flex-1 min-h-0 flex ${showCoveragePanel ? 'flex-col lg:flex-row' : 'flex-col'}`}>
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trading day</label>
                <input
                  type="date"
                  className="input-field w-full"
                  value={tradingDay}
                  onChange={(e) => setTradingDay(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Gaming day (folder delivery date − 1)</p>
              </div>
              <div className="flex items-end">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={preparePipeline}
                      onChange={(e) => setPreparePipeline(e.target.checked)}
                    />
                    Prepare pipeline (enrich + allocate) if not ready
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={prepareRollup}
                      onChange={(e) => setPrepareRollup(e.target.checked)}
                    />
                    Prepare rollups (member stats + transaction rollup) for cumulative rules
                  </label>
                </div>
              </div>
            </div>

            <div className="rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
              <strong>Performance:</strong> session and interval rules each scan up to 5000 rows
              (sessions vs 15‑min attribution intervals). Cost is roughly rows × rules of in-memory
              checks — not a separate pipeline per rule. Uncheck “Prepare pipeline” when preflight
              is already green to avoid the slow path.
            </div>

            <div className="rounded border border-gray-200 p-3 bg-gray-50 text-sm">
              <div className="font-medium text-gray-800 mb-2">Preflight</div>
              {loadingPreflight && <div className="text-gray-500">Checking data readiness…</div>}
              {!loadingPreflight && preflight && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Stat label="Sessions" value={preflight.sessions?.count} ok={preflight.sessions?.ok} />
                    <Stat label="Meter" value={preflight.meter?.count} ok={preflight.meter?.ok} />
                    <Stat label="Enriched" value={preflight.enriched?.count} ok={preflight.enriched?.ok} />
                    <Stat label="Attribution" value={preflight.attribution?.count} ok={preflight.attribution?.ok} />
                    <Stat label="Txn rollup" value={preflight.rollup?.member_transaction_rollup?.count} ok={preflight.rollup?.member_transaction_rollup?.ok} />
                    <Stat label="Daily stats" value={preflight.rollup?.member_stats_daily?.count} ok={preflight.rollup?.member_stats_daily?.ok} />
                  </div>
                  <div className="text-gray-600">
                    Pipeline: <span className="font-mono">{preflight.pipeline_state || '—'}</span>
                    {' · '}
                    Rules ready: <span className={preflight.rules_ready ? 'text-green-700' : 'text-amber-700'}>
                      {preflight.rules_ready ? 'yes' : 'no'}
                    </span>
                  </div>
                  {blocking.length > 0 && (
                    <ul className="text-red-700 list-disc pl-5">
                      {blocking.map((b) => <li key={b}>{b}</li>)}
                    </ul>
                  )}
                  {warnings.length > 0 && (
                    <ul className="text-amber-800 list-disc pl-5">
                      {warnings.map((w) => <li key={w}>{w}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {result && (
              <div className="rounded border border-gray-200 p-3 text-sm">
                <div className="font-medium mb-2">
                  Results
                  {result.live_run ? (
                    <span className="ml-2 text-xs font-normal text-amber-800">
                      {(result.summary?.alerts_staged ?? 0) > 0
                        ? `(live — ${result.summary.alerts_staged} staged)`
                        : '(live — no alerts staged)'}
                    </span>
                  ) : (
                    <span className="ml-2 text-xs font-normal text-gray-500">(dry-run — no alerts)</span>
                  )}
                </div>
                {result.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    <div>Session rules: <strong>{result.summary.rules_run}</strong></div>
                    <div>Interval rules: <strong>{result.summary.interval_rules_run ?? 0}</strong></div>
                    <div>Cumulative rules: <strong>{result.summary.cumulative_rules_run ?? 0}</strong></div>
                    <div>Sessions evaluated: <strong>{result.summary.records_evaluated}</strong></div>
                    <div>Intervals evaluated: <strong>{result.summary.intervals_evaluated ?? 0}</strong></div>
                    <div>Sessions triggered: <strong>{result.summary.sessions_triggered ?? 0}</strong></div>
                    <div>Intervals triggered: <strong>{result.summary.intervals_triggered ?? 0}</strong></div>
                    <div>Members triggered: <strong>{result.summary.members_triggered ?? 0}</strong></div>
                    <div className="col-span-2">Total hits: <strong>{result.summary.total_hits}</strong></div>
                    {result.live_run && (
                      <>
                        <div>Alerts staged: <strong>{result.summary.alerts_staged ?? 0}</strong></div>
                        <div>Skipped (already open): <strong>{result.summary.alerts_skipped_existing ?? 0}</strong></div>
                        {(result.summary.alerts_skipped_failed ?? 0) > 0 && (
                          <div className="col-span-2 text-red-700">
                            Staging failed: <strong>{result.summary.alerts_skipped_failed}</strong>
                            {Array.isArray(result.summary.persist_errors) && result.summary.persist_errors[0] && (
                              <span className="block text-xs font-normal mt-0.5">
                                {result.summary.persist_errors[0]}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {result.consolidation && !result.consolidation.error && (
                  <p className="text-xs text-gray-600 mb-2">
                    Consolidation: created {result.consolidation.created ?? 0}, updated{' '}
                    {result.consolidation.updated ?? 0}, suppressed {result.consolidation.suppressed ?? 0}
                  </p>
                )}
                {result.consolidation?.error && (
                  <p className="text-xs text-amber-800 mb-2">
                    Consolidation deferred (5‑min job): {result.consolidation.error}
                  </p>
                )}
                {Array.isArray(result.interval_results) && result.interval_results.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap items-end justify-between gap-2 mb-1">
                      <div className="text-xs font-medium text-gray-600">
                        Interval triggers (15 min) — click a row for amounts / open in Gaming Data
                        {houseFilter.trim() && (
                          <span className="ml-1 font-normal text-gray-500">
                            ({filteredIntervalResults.length} of {result.interval_results.length})
                          </span>
                        )}
                      </div>
                      {!result.results?.length && (
                        <div className="min-w-[10rem]">
                          <label className="block text-[10px] text-gray-500 mb-0.5">Filter house / EGM</label>
                          <input
                            className="input-field !py-1 !px-2 !text-xs w-full"
                            placeholder="010234 or 234"
                            value={houseFilter}
                            onChange={(e) => setHouseFilter(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="max-h-[min(28rem,50vh)] overflow-y-auto border rounded">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left w-6" />
                            <th className="px-2 py-1 text-left">Interval start</th>
                            <th className="px-2 py-1 text-left">EGM</th>
                            <th className="px-2 py-1 text-left">Session</th>
                            <th className="px-2 py-1 text-right">CashIn</th>
                            <th className="px-2 py-1 text-right">Turnover</th>
                            <th className="px-2 py-1 text-left">Rules</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredIntervalResults.slice(0, 100).map((r) => {
                            const rowKey = intervalRowKey(r)
                            const openRow = expandedInterval === rowKey
                            const ev = r.evidence || {}
                            const { from, to } = intervalWindowIso(r.interval_start, r.interval_end)
                            const combinedUrl = buildGamingDataUrl({
                              tab: 'combined',
                              egm_pk: r.egm_pk,
                              member_id: r.member_id,
                              from,
                              to,
                              trading_day: r.gaming_date || tradingDay,
                            })
                            const sessionsUrl = buildGamingDataUrl({
                              tab: 'sessions',
                              egm_pk: r.egm_pk,
                              member_id: r.member_id,
                              from,
                              to,
                            })
                            const houseLabel = [r.house_number, r.egm_pk].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') || r.egm_pk || '—'
                            return (
                              <React.Fragment key={rowKey}>
                                <tr
                                  className="border-t hover:bg-gray-50 cursor-pointer"
                                  onClick={() => setExpandedInterval(openRow ? null : rowKey)}
                                >
                                  <td className="px-2 py-1 text-gray-400">
                                    {openRow
                                      ? <ChevronDownIcon className="h-3.5 w-3.5" />
                                      : <ChevronRightIcon className="h-3.5 w-3.5" />}
                                  </td>
                                  <td className="px-2 py-1 whitespace-nowrap">
                                    {r.interval_start ? new Date(r.interval_start).toLocaleString() : '—'}
                                  </td>
                                  <td className="px-2 py-1 font-mono text-[11px]">{houseLabel}</td>
                                  <td className="px-2 py-1 font-mono text-[11px] max-w-[8rem] truncate" title={r.session_pk}>
                                    {r.session_pk || '—'}
                                  </td>
                                  <td className="px-2 py-1 text-right font-medium">{fmtMoney(ev.CashIn)}</td>
                                  <td className="px-2 py-1 text-right">{fmtMoney(ev.Turnover)}</td>
                                  <td className="px-2 py-1">
                                    {(r.rules || []).map((x) => x.id || x.name).join(', ') || r.error || '—'}
                                  </td>
                                </tr>
                                {openRow && (
                                  <tr className="border-t bg-slate-50">
                                    <td colSpan={7} className="px-3 py-3">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                                        <Ev label="Interval start" value={r.interval_start ? new Date(r.interval_start).toLocaleString() : '—'} />
                                        <Ev label="Interval end" value={r.interval_end ? new Date(r.interval_end).toLocaleString() : '—'} />
                                        <Ev label="CashIn (interval)" value={fmtMoney(ev.CashIn)} />
                                        <Ev label="CashDrop" value={fmtMoney(ev.CashDrop)} />
                                        <Ev label="Turnover (interval)" value={fmtMoney(ev.Turnover)} />
                                        <Ev label="Stroke" value={fmtNum(ev.Stroke)} />
                                        <Ev label="CashIn / Turnover" value={ev.cash_in_over_turnover != null ? `${fmtNum(ev.cash_in_over_turnover)}×` : '—'} />
                                        <Ev label="Member" value={r.member_id && r.member_id !== '__UNCARDED__' ? r.member_id : 'Uncarded'} />
                                        <Ev label="House / EGM" value={houseLabel} />
                                        <Ev label="Venue" value={r.venue || '—'} />
                                        <Ev label="Gaming date" value={r.gaming_date ? String(r.gaming_date).slice(0, 10) : '—'} />
                                        <Ev label="Session PK" value={r.session_pk || '—'} />
                                      </div>
                                      <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                        <Link
                                          to={combinedUrl}
                                          className="inline-flex items-center rounded border border-psg-teal/40 bg-white px-2.5 py-1 text-xs font-medium text-psg-teal hover:bg-psg-teal/5"
                                          onClick={onClose}
                                        >
                                          Open in Gaming Data (combined)
                                        </Link>
                                        <Link
                                          to={sessionsUrl}
                                          className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                          onClick={onClose}
                                        >
                                          Open sessions view
                                        </Link>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {Array.isArray(result.results) && result.results.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap items-end justify-between gap-2 mb-1">
                      <div className="text-xs font-medium text-gray-600">
                        Session triggers — click a row for amounts / open in Gaming Data
                        {houseFilter.trim() && (
                          <span className="ml-1 font-normal text-gray-500">
                            ({filteredResults.length} of {result.results.length})
                          </span>
                        )}
                      </div>
                      <div className="min-w-[10rem]">
                        <label className="block text-[10px] text-gray-500 mb-0.5">Filter house / EGM</label>
                        <input
                          className="input-field !py-1 !px-2 !text-xs w-full"
                          placeholder="010038 or 38"
                          value={houseFilter}
                          onChange={(e) => setHouseFilter(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="max-h-[min(28rem,50vh)] overflow-y-auto border rounded">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left w-6" />
                            <th className="px-2 py-1 text-left">Session</th>
                            <th className="px-2 py-1 text-left">Member</th>
                            <th className="px-2 py-1 text-right">CashIn</th>
                            <th className="px-2 py-1 text-right">Turnover</th>
                            <th className="px-2 py-1 text-left">Hits</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredResults.slice(0, 50).map((r) => {
                            const openRow = expandedSession === r.session_pk
                            const ev = r.evidence || {}
                            const { from, to } = sessionWindowIso(r.start_time, r.end_time)
                            const sessionsUrl = buildGamingDataUrl({
                              tab: 'sessions',
                              egm_pk: r.egm_pk,
                              member_id: r.member_id,
                              from,
                              to,
                            })
                            const combinedUrl = buildGamingDataUrl({
                              tab: 'combined',
                              egm_pk: r.egm_pk,
                              member_id: r.member_id,
                              from,
                              to,
                            })
                            const profileUrl = r.member_id
                              ? `/profile-safe/search?memberId=${encodeURIComponent(r.member_id)}`
                              : null
                            return (
                              <React.Fragment key={r.session_pk}>
                                <tr
                                  className="border-t hover:bg-gray-50 cursor-pointer"
                                  onClick={() => setExpandedSession(openRow ? null : r.session_pk)}
                                >
                                  <td className="px-2 py-1 text-gray-400">
                                    {openRow
                                      ? <ChevronDownIcon className="h-3.5 w-3.5" />
                                      : <ChevronRightIcon className="h-3.5 w-3.5" />}
                                  </td>
                                  <td className="px-2 py-1 font-mono text-[11px]">{r.session_pk}</td>
                                  <td className="px-2 py-1">{r.member_id || '—'}</td>
                                  <td className="px-2 py-1 text-right font-medium">{fmtMoney(ev.CashIn)}</td>
                                  <td className="px-2 py-1 text-right">{fmtMoney(ev.Turnover)}</td>
                                  <td className="px-2 py-1">
                                    {(r.rules || []).map((x) => x.id || x.name).join(', ') || r.error || '—'}
                                  </td>
                                </tr>
                                {openRow && (
                                  <tr className="border-t bg-slate-50">
                                    <td colSpan={6} className="px-3 py-3">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                                        <Ev label="CashIn (cashin+cashdrop)" value={fmtMoney(ev.CashIn)} />
                                        <Ev label="CashDrop" value={fmtMoney(ev.CashDrop)} />
                                        <Ev label="CashlessIn" value={fmtMoney(ev.CashlessIn)} />
                                        <Ev label="Turnover" value={fmtMoney(ev.Turnover)} />
                                        <Ev label="Stroke" value={fmtNum(ev.Stroke)} />
                                        <Ev label="CashIn / Turnover" value={ev.cash_in_over_turnover != null ? `${fmtNum(ev.cash_in_over_turnover)}×` : '—'} />
                                        <Ev label="Duration (min)" value={fmtNum(r.duration_minutes)} />
                                        <Ev
                                          label="House / EGM"
                                          value={[r.house_number, r.egm_pk].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') || '—'}
                                        />
                                        <Ev label="Venue" value={r.venue || '—'} />
                                        <Ev label="Start" value={r.start_time ? new Date(r.start_time).toLocaleString() : '—'} />
                                        <Ev label="End" value={r.end_time ? new Date(r.end_time).toLocaleString() : '—'} />
                                        <Ev label="Gaming date" value={r.gaming_date ? String(r.gaming_date).slice(0, 10) : '—'} />
                                      </div>
                                      <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                        <Link
                                          to={sessionsUrl}
                                          className="inline-flex items-center rounded border border-psg-teal/40 bg-white px-2.5 py-1 text-xs font-medium text-psg-teal hover:bg-psg-teal/5"
                                          onClick={onClose}
                                        >
                                          Open in Gaming Data (sessions)
                                        </Link>
                                        <Link
                                          to={combinedUrl}
                                          className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                          onClick={onClose}
                                        >
                                          Open combined / attribution
                                        </Link>
                                        {profileUrl && (
                                          <Link
                                            to={profileUrl}
                                            className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                            onClick={onClose}
                                          >
                                            Member profile
                                          </Link>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {Array.isArray(result.cumulative_results) && result.cumulative_results.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-1">Cumulative triggers (by member)</div>
                    <div className="max-h-36 overflow-y-auto border rounded">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-1 text-left">Member</th>
                            <th className="px-2 py-1 text-left">Rules</th>
                            <th className="px-2 py-1 text-left">Open</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.cumulative_results.slice(0, 30).map((r) => (
                            <tr key={r.member_id} className="border-t">
                              <td className="px-2 py-1">{r.member_id}</td>
                              <td className="px-2 py-1">
                                {(r.rules || []).map((x) => x.id || x.name).join(', ')}
                              </td>
                              <td className="px-2 py-1">
                                <Link
                                  to={`/profile-safe/search?memberId=${encodeURIComponent(r.member_id)}`}
                                  className="text-psg-teal hover:underline"
                                  onClick={onClose}
                                >
                                  Profile
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {result.ok
                  && !result.results?.length
                  && !result.interval_results?.length
                  && !result.cumulative_results?.length && (
                  <p className="text-gray-600">No rule triggers for this trading day.</p>
                )}
                {!result.ok && (
                  <p className="text-red-700">{result.error || 'Run did not complete'}</p>
                )}
              </div>
            )}
          </div>

          {showCoveragePanel && (
            <aside className="w-full lg:w-80 xl:w-96 shrink-0 min-h-[14rem] lg:min-h-0 max-h-64 lg:max-h-none overflow-hidden flex flex-col">
              <RuleCoveragePanel coverage={coverage} />
            </aside>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-2 shrink-0">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={running}>
            Close
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-2"
            disabled={running || !tradingDay}
            onClick={() => handleRun(false)}
          >
            <PlayIcon className="h-4 w-4" />
            {running && runMode === 'dry' ? 'Running…' : 'Run dry-run'}
          </button>
          {!draftOnly && (
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              disabled={running || !tradingDay}
              onClick={() => handleRun(true)}
              title="Creates Monitor Safe alerts for hits (skips sessions that already have an open alert for the same rule)"
            >
              <BoltIcon className="h-4 w-4" />
              {running && runMode === 'live' ? 'Running live…' : 'Run live'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value, ok }) {
  return (
    <div className={`rounded px-2 py-1 ${ok ? 'bg-green-50 text-green-900' : 'bg-gray-100 text-gray-700'}`}>
      <div className="text-xs opacity-75">{label}</div>
      <div className="font-semibold">{value ?? '—'}</div>
    </div>
  )
}

function Ev({ label, value }) {
  return (
    <div className="rounded border border-gray-200 bg-white px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="font-medium text-gray-900 break-all">{value}</div>
    </div>
  )
}
